import { useEffect, useState } from "react";
import { UserPlus, Trash, ShieldCheck, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setItems(await api.listAllowedEmails());
    } catch (e) {
      toast.error("Error al cargar usuarios");
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await api.addAllowedEmail({ email: email.trim(), role });
      toast.success(`${email.trim()} ahora tiene acceso`);
      setEmail("");
      setRole("member");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Correo inválido");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (it) => {
    if (!window.confirm(`¿Quitar el acceso a ${it.email}? Su sesión se cerrará.`)) return;
    try {
      await api.removeAllowedEmail(it.email);
      toast.success("Acceso eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al quitar acceso");
    }
  };

  return (
    <div>
      <div className="h-16 border-b border-border px-6 flex items-center bg-card">
        <div>
          <div className="label-xs text-muted-foreground">Administración</div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight">Usuarios con acceso</h1>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-4xl">
        <form onSubmit={handleAdd} className="card-elevated p-5 flex flex-col md:flex-row gap-3 md:items-center" data-testid="add-user-form">
          <Input data-testid="allowed-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="nombre@gmail.com" className="rounded-sm flex-1" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="allowed-role-select" className="rounded-sm md:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Miembro</SelectItem>
              <SelectItem value="admin">Administrador</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="add-allowed-email-btn" type="submit" disabled={saving} className="rounded-sm font-semibold">
            <UserPlus size={16} weight="bold" className="mr-2" /> Dar acceso
          </Button>
        </form>

        <div className="card-elevated overflow-x-auto">
          <table className="w-full text-sm table-row-hover" data-testid="allowed-emails-table">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-2 label-xs text-muted-foreground">Correo</th>
                <th className="text-left px-4 py-2 label-xs text-muted-foreground">Rol</th>
                <th className="text-left px-4 py-2 label-xs text-muted-foreground">Último ingreso</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.email} className="border-b border-border" data-testid={`allowed-row-${it.email}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{it.email}</div>
                    {it.name && <div className="text-xs text-muted-foreground">{it.name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 label-xs px-2 py-0.5 rounded-sm bg-muted">
                      {it.role === "admin" ? <ShieldCheck size={11} weight="fill" /> : <User size={11} />}
                      {it.role === "admin" ? "Admin" : "Miembro"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground font-mono-tabular">
                    {it.last_login ? new Date(it.last_login).toLocaleString("es-ES") : "Pendiente"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!it.is_owner && it.email !== user?.email && (
                      <button data-testid={`remove-allowed-${it.email}`} onClick={() => handleRemove(it)}
                        className="p-1.5 rounded-sm text-muted-foreground hover:text-destructive hover:bg-muted transition-colors">
                        <Trash size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
